/**
 * Story Index Service
 * 
 * Service for loading and accessing story metadata from the generated index.json file.
 * This replaces the hardcoded STORY_CONFIG with dynamic data.
 */

import type { Language } from '@/config';

/**
 * Story metadata from index.json
 */
export interface StoryIndexEntry {
  id: string; // e.g., "fr/les-gardiens-de-la-magie-oubliee"
  story_id: string; // e.g., "keepers-of-the-forgotten-magic"
  language: Language;
  folder: string;
  title: string;
  description: string;
  author: string;
  total_episodes: number;
  total_duration: string;
  total_size_mb: number;
  cover_image: string;
  banner_image: string;
}

/**
 * Story index structure
 */
export interface StoryIndex {
  version: string;
  generated_at: string;
  total_stories: number;
  stories: StoryIndexEntry[];
}

/**
 * Normalized story configuration (compatible with old STORY_CONFIG)
 */
export interface StoryConfig {
  id: string;
  folders: Record<Language, string>;
  totalEpisodes: number;
  titles: Record<Language, string>;
  descriptions: Record<Language, string>;
}

/**
 * StoryIndexService class for managing story metadata
 */
export class StoryIndexService {
  private index: StoryIndex | null = null;
  private configMap: Record<string, StoryConfig> | null = null;

  /**
   * Load the story index from index.json
   */
  async loadIndex(): Promise<StoryIndex> {
    if (this.index) {
      return this.index;
    }

    const response = await fetch('/stories/index.json');
    if (!response.ok) {
      throw new Error(`Failed to load story index: ${response.statusText}`);
    }

    const data: StoryIndex = await response.json();
    this.index = data;
    this.buildConfigMap();
    return data;
  }

  /**
   * Build a normalized config map from the index
   * Groups stories by story_id and organizes by language
   */
  private buildConfigMap(): void {
    if (!this.index) return;

    const map: Record<string, StoryConfig> = {};

    // Group stories by story_id
    for (const entry of this.index.stories) {
      if (!map[entry.story_id]) {
        map[entry.story_id] = {
          id: entry.story_id,
          folders: {} as Record<Language, string>,
          totalEpisodes: entry.total_episodes,
          titles: {} as Record<Language, string>,
          descriptions: {} as Record<Language, string>,
        };
      }

      const config = map[entry.story_id];
      config.folders[entry.language] = entry.folder;
      config.titles[entry.language] = entry.title;
      config.descriptions[entry.language] = entry.description;
    }

    this.configMap = map;
  }

  /**
   * Get all story IDs
   */
  getStoryIds(): string[] {
    if (!this.configMap) {
      console.warn('Story index not loaded yet');
      return [];
    }
    return Object.keys(this.configMap);
  }

  /**
   * Get story configuration by ID
   */
  getStoryConfig(storyId: string): StoryConfig | undefined {
    return this.configMap?.[storyId];
  }

  /**
   * Check if a story ID is valid
   */
  isValidStoryId(storyId: string): boolean {
    return !!this.configMap?.[storyId];
  }

  /**
   * Get story entry for a specific language
   */
  getStoryEntry(storyId: string, language: Language): StoryIndexEntry | undefined {
    if (!this.index) return undefined;
    return this.index.stories.find(
      (entry) => entry.story_id === storyId && entry.language === language
    );
  }

  /**
   * Get all stories for a specific language
   */
  getStoriesByLanguage(language: Language): StoryIndexEntry[] {
    if (!this.index) return [];
    return this.index.stories.filter((entry) => entry.language === language);
  }

  /**
   * Get the normalized config map (compatible with old STORY_CONFIG)
   */
  getConfigMap(): Record<string, StoryConfig> {
    if (!this.configMap) {
      console.warn('Story index not loaded yet');
      return {};
    }
    return this.configMap;
  }

  /**
   * Find story ID by folder slug and language
   */
  getStoryIdBySlug(slug: string, language: Language): string | undefined {
    if (!this.index) return undefined;
    const entry = this.index.stories.find(
      (entry) => entry.folder === slug && entry.language === language
    );
    return entry?.story_id;
  }

  /**
   * Find story folder by story ID and language
   */
  getStoryFolder(storyId: string, language: Language): string | undefined {
    const config = this.getStoryConfig(storyId);
    return config?.folders[language];
  }
}

/**
 * Default StoryIndexService instance
 */
export const storyIndexService = new StoryIndexService();
